// 库存与采购的状态文案和颜色。
// 分类口径照老系统（src/app/(app)/inventory/page.tsx getTransactionTypeTag / getInventoryAvailability、
// purchase-orders/page.tsx statusConfig）；颜色按文档 18 §4 收敛：antd 的 cyan→blue、purple→violet、gold→amber，
// 终结态（已取消）归灰。一行只放一个实底标签，次级状态用色点。
import type { TagTone } from '@/lib/status';
import { num } from '@/lib/format';
import type { InventoryTransaction, PurchaseStatus, StockStatusKey, TxSource } from './types';

/* ------------------------------ 采购单 ------------------------------ */

export const purchaseStatus: Record<PurchaseStatus, { label: string; tone: TagTone }> = {
  DRAFT: { label: '草稿', tone: 'none' },
  ORDERED: { label: '已下单', tone: 'blue' },
  PARTIALLY_RECEIVED: { label: '部分入库', tone: 'amber' },
  RECEIVED: { label: '已入库', tone: 'green' },
  CANCELLED: { label: '已取消', tone: 'none' },
};

/** 还在等到货的状态（老系统「转入库」按钮的出现条件） */
export const awaitingStatuses: PurchaseStatus[] = ['ORDERED', 'PARTIALLY_RECEIVED'];

export const auditAction: Record<string, string> = {
  'purchase.create': '新建采购单',
  'purchase.update': '调整采购单',
  'purchase.receive': '转入库',
  'purchase.reject': '到货拒收',
  'purchase.return': '采购退货',
  'purchase.closeRemaining': '关闭剩余',
  'purchase.cancel': '取消采购单',
  'purchase.delete': '删除采购单',
};

/* ------------------------------ 库存 ------------------------------ */

/** 库存状态筛选（/api/inventory/summary?status=），文案照老系统侧栏 */
export const stockViews: { key: StockStatusKey; label: string; hint: string }[] = [
  { key: 'all', label: '全部库存', hint: '全部产品' },
  { key: 'shortage', label: '可用为负', hint: '预占大于现有：已接单未到货' },
  { key: 'reserved', label: '有预占', hint: '被未发货订单占用' },
  { key: 'inStock', label: '有实际库存', hint: '现有 > 0' },
  { key: 'negativeActual', label: '实际为负', hint: '现有 < 0，需要盘点' },
  { key: 'noStock', label: '无实际库存', hint: '现有 ≤ 0' },
];

/**
 * 单个产品的库存提示（老系统 getInventoryAvailability）。
 * 负数不拦（文档 23 §11）：只标红提示，列表里的任何按钮照常可用。
 */
export function availability(current: unknown, available: unknown): { label: string; tone: 'red' | 'amber' | 'green'; solid: boolean } {
  const c = num(current);
  const a = num(available);
  if (c < 0) return { label: '实际为负', tone: 'red', solid: true };
  if (a < 0) return { label: '负可用', tone: 'red', solid: true };
  if (a === 0) return { label: '可用为零', tone: 'amber', solid: false };
  if (a < Math.max(10, c * 0.2)) return { label: '库存较低', tone: 'amber', solid: false };
  return { label: '库存充足', tone: 'green', solid: false };
}

/* ------------------------------ 出入库流水 ------------------------------ */

/** 流水类型：逐条照老系统 getTransactionTypeTag 的判断顺序 */
export function transactionKind(t: Pick<InventoryTransaction, 'type' | 'direction' | 'referenceType' | 'note'>): { label: string; tone: TagTone } {
  const note = t.note || '';
  if (t.type === 'STOCK_IN') {
    if (t.referenceType === 'InitialStockImport') return { label: '期初导入', tone: 'blue' };
    if (t.referenceType === 'TaobaoOrderReturn') return { label: '淘宝店退货入库', tone: 'blue' };
    return { label: '手动入库', tone: 'blue' };
  }
  if (t.type === 'PURCHASE_IN') return note.startsWith('手动入库') ? { label: '手动入库', tone: 'blue' } : { label: '采购入库', tone: 'green' };
  if (t.type === 'RESERVE') return { label: '订单预占', tone: 'violet' };
  if (t.type === 'RELEASE') return { label: '解除预占', tone: 'none' };
  if (t.type === 'SALE_OUT') return { label: t.referenceType === 'TaobaoOrder' ? '淘宝店销售出库' : '发货出库', tone: 'orange' };
  if (t.type === 'ADJUST' && (t.direction === 'OUT' || /破损|补耗|盘亏|报损/.test(note))) return { label: '报损/盘亏', tone: 'red' };
  if (t.type === 'ADJUST') return { label: '手工调整', tone: 'amber' };
  return { label: typeLabel[t.type] || t.type, tone: 'none' };
}

const typeLabel: Record<string, string> = {
  PURCHASE_IN: '采购入库',
  STOCK_IN: '库存入库',
  SALE_OUT: '发货出库',
  RESERVE: '库存预占',
  RELEASE: '解除预占',
  ADJUST: '库存调整',
};

/**
 * 数量怎么写：入库 +、出库 −；预占 / 释放不动实物，不带符号；
 * 期初导入与「盘点无差异」的 direction=ADJUST，quantity 本身带符号。
 */
export function signedQuantity(t: Pick<InventoryTransaction, 'direction' | 'quantity'>): { value: number; sign: '+' | '−' | ''; tone: 'in' | 'out' | 'plain' } {
  const q = num(t.quantity);
  if (t.direction === 'IN') return { value: Math.abs(q), sign: '+', tone: 'in' };
  if (t.direction === 'OUT') return { value: Math.abs(q), sign: '−', tone: 'out' };
  if (t.direction === 'ADJUST') {
    if (q > 0) return { value: q, sign: '+', tone: 'in' };
    if (q < 0) return { value: -q, sign: '−', tone: 'out' };
    return { value: 0, sign: '', tone: 'plain' };
  }
  return { value: Math.abs(q), sign: '', tone: 'plain' };
}

/** 来源筛选（/api/inventory/transactions?source=），顺序与文案照老系统 */
export const txSources: { key: TxSource; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'purchaseIn', label: '采购入库' },
  { key: 'manualIn', label: '手动入库' },
  { key: 'initialImport', label: '期初导入' },
  { key: 'taobaoReturn', label: '淘宝店退货入库' },
  { key: 'orderReserve', label: '订单预占' },
  { key: 'orderRelease', label: '解除预占' },
  { key: 'orderOut', label: '发货出库' },
  { key: 'taobaoOut', label: '淘宝店销售出库' },
  { key: 'damageOut', label: '报损/盘亏' },
  { key: 'manualAdjust', label: '手工调整' },
];

/** 时间范围：老系统流水页默认近 30 天；接口不给 from 时默认 90 天，all=1 才是全部 */
export const txRanges: { key: string; label: string; days?: number }[] = [
  { key: '7', label: '近 7 天', days: 7 },
  { key: '30', label: '近 30 天', days: 30 },
  { key: '90', label: '近 90 天', days: 90 },
  { key: 'all', label: '全部时间' },
];
